import { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Users, MessageSquare, UserPlus, LogOut, Search, RefreshCw, Shield, Star, Award, User, UserCircle, Lock, AlertTriangle, Globe, MessageCircle, HelpCircle, Maximize, Minimize, Diamond, CreditCard, Heart, Gift, Crown, Film, Play, ChevronDown, Trophy, Medal, Vote, Mail, ClipboardList, ChevronRight, Menu } from "lucide-react";
import { toast } from "sonner";
import { API, AuthContext, PMContext } from "../App";
import FriendsPanel from "../components/FriendsPanel";
import PrivateMessagesPanel from "../components/PrivateMessagesPanel";
import AnchoredPopover from "../components/AnchoredPopover";
import AdminPanel from "../components/AdminPanel";
import StatusSelector from "../components/StatusSelector";
import MenuBar from "../components/MenuBar";
import MyAccountPanel from "../components/MyAccountPanel";
import MessengerPage from "./MessengerPage";
import DraggableResizableWindow from "../components/DraggableResizableWindow";
import DonateModal from "../components/DonateModal";
import GuestUpgradeModal from "../components/GuestUpgradeModal";
import AvatarBuilder from "../components/AvatarBuilder";
import UserSearchModal from "../components/UserSearchModal";
import VotingPanel from "../components/VotingPanel";
import TournamentPanel from "../components/TournamentPanel";
import LeaderboardPanel from "../components/LeaderboardPanel";
import BadgesPanel from "../components/BadgesPanel";
import StaffMailPanel from "../components/StaffMailPanel";
import StaffTasksPanel from "../components/StaffTasksPanel";
import AdminAnnouncementPopup from "../components/AdminAnnouncementPopup";
import OfflineMessagesPopup from "../components/OfflineMessagesPopup";
import { MobileMenu, MobileSheet } from "../components/MobileMenu";
import { AliasManager, RoomIdentityPicker } from "../components/AliasManager";
import { TOSModal, HelpModal, ContactModal } from "../components/HelpPages";

const LobbyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, logout } = useContext(AuthContext);
  const { openPMWindow } = useContext(PMContext); // Use global PM context
  const [roomsData, setRoomsData] = useState({ categories: {}, capacity: 60 });
  const [searchTerm, setSearchTerm] = useState("");
  const [showFriends, setShowFriends] = useState(false);
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showApplicationModal, setShowApplicationModal] = useState(null);
  const [applicationReason, setApplicationReason] = useState("");
  const [submittingApplication, setSubmittingApplication] = useState(false);
  const [activeTab, setActiveTab] = useState("entertainment");
  // Guests can only enter GUEST CHAT (under the Welcome category) — land them there.
  useEffect(() => {
    if (user?.is_guest) setActiveTab("welcome");
  }, [user?.is_guest]);
  const [showAccessDenied, setShowAccessDenied] = useState(null);
  const [showMyAccount, setShowMyAccount] = useState(false);
  const [showMessenger, setShowMessenger] = useState(false);
  const [messengerUnread, setMessengerUnread] = useState(0);
  const [isMessengerMobile, setIsMessengerMobile] = useState(false);
  const [isMobileNav, setIsMobileNav] = useState(typeof window !== "undefined" ? window.innerWidth < 768 : false);
  const [showGuestUpgrade, setShowGuestUpgrade] = useState(false);
  const [showAvatarBuilder, setShowAvatarBuilder] = useState(false);

  useEffect(() => {
    const check = () => {
      setIsMessengerMobile(window.innerWidth < 640);
      setIsMobileNav(window.innerWidth < 768);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  // Load the user's aliases so we can offer the room-entry identity picker.
  const loadAliases = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/aliases`, { headers: { Authorization: `Bearer ${token}` } });
      setMyAliases(res.data?.aliases || []);
    } catch (e) { /* ignore */ }
  }, [token]);
  useEffect(() => { if (token) loadAliases(); }, [token, loadAliases]);
  // Lock page scroll while the mobile Messenger overlay is open so the whole
  // document can't scroll the composer off the bottom of the screen.
  useEffect(() => {
    if (showMessenger && isMessengerMobile) {
      const html = document.documentElement;
      const body = document.body;
      const prevHtml = html.style.overflow;
      const prevBody = body.style.overflow;
      const scrollY = window.scrollY;
      html.style.overflow = "hidden";
      body.style.overflow = "hidden";
      return () => {
        html.style.overflow = prevHtml;
        body.style.overflow = prevBody;
        window.scrollTo(0, scrollY);
      };
    }
  }, [showMessenger, isMessengerMobile]);

  // Poll unread messenger count → in-app badge + PWA app-icon badge (Badging API)
  useEffect(() => {
    if (!token) return;
    let alive = true;
    const fetchUnread = async () => {
      try {
        const res = await axios.get(`${API}/messenger/unread-count`, { headers: { Authorization: `Bearer ${token}` } });
        if (!alive) return;
        const n = res.data?.unread || 0;
        setMessengerUnread(n);
        if ("setAppBadge" in navigator) {
          try { n > 0 ? navigator.setAppBadge(n) : navigator.clearAppBadge(); } catch (_) { /* noop */ }
        }
      } catch (_) { /* noop */ }
    };
    fetchUnread();
    const iv = setInterval(fetchUnread, 20_000);
    return () => { alive = false; clearInterval(iv); };
  }, [token, showMessenger]);
  const [myAccountInitialTab, setMyAccountInitialTab] = useState("overview");
  const [showDonateModal, setShowDonateModal] = useState(false);
  const [showTOS, setShowTOS] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showEliteVIPMemo, setShowEliteVIPMemo] = useState(false);
  const [eliteVIPMemo, setEliteVIPMemo] = useState("");
  const [movieNowPlaying, setMovieNowPlaying] = useState(null);
  const [showExtrasMenu, setShowExtrasMenu] = useState(false);
  const [activeExtrasPanel, setActiveExtrasPanel] = useState(null); // 'voting', 'tournaments', 'leaderboard', 'badges'
  const [showStaffMail, setShowStaffMail] = useState(false);
  const [showStaffTasks, setShowStaffTasks] = useState(false);
  const [expandedSubcategory, setExpandedSubcategory] = useState(null); // For entertainment subcategories
  const [openMenuCat, setOpenMenuCat] = useState(null); // Category whose pop-out room dropdown is open
  const [showAliasManager, setShowAliasManager] = useState(false);
  const [myAliases, setMyAliases] = useState([]);
  const [pendingRoom, setPendingRoom] = useState(null); // room awaiting identity choice
  const extrasMenuRef = useRef(null);
  const friendsBtnRef = useRef(null);
  const messagesBtnRef = useRef(null);
  
  // Popup states for admin announcements and offline messages
  const [showAdminAnnouncements, setShowAdminAnnouncements] = useState(true); // Show on load
  const [showOfflineMessages, setShowOfflineMessages] = useState(true); // Show on login if any exist

  // Unread PM badge count — polled every 15s so the Messages button shows a red badge
  // with the number of new messages waiting.
  const [pmUnreadCount, setPmUnreadCount] = useState(0);
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const res = await axios.get(`${API}/messages/conversations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (cancelled) return;
        const total = (res.data || []).reduce((sum, c) => sum + (c.unread_count || 0), 0);
        setPmUnreadCount(total);
      } catch {
        // Ignore — banner just won't update this poll
      }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [token]);

  // Fullscreen toggle
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(err => {
        toast.error("Fullscreen not supported");
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      });
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Close extras menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (extrasMenuRef.current && !extrasMenuRef.current.contains(e.target)) {
        setShowExtrasMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle opening a PM (use global context)
  const handleOpenPM = useCallback((targetUser) => {
    openPMWindow(targetUser.user_id, targetUser.username);
  }, [openPMWindow]);

  // Check for PM to open from navigation state (from profile page)
  useEffect(() => {
    if (location.state?.openPmWith) {
      handleOpenPM(location.state.openPmWith);
      // Clear the state
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, handleOpenPM, navigate, location.pathname]);

  const fetchRooms = async () => {
    try {
      const response = await axios.get(`${API}/rooms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoomsData(response.data);
    } catch (error) {
      console.error("Failed to fetch rooms:", error);
    } finally {
      setLoading(false);
    }
  };

  // Manual refresh triggered from File → Refresh Rooms (or F5). Also forces a
  // server-side zombie-connection sweep so user counts settle to their true value.
  const fetchRoomsManual = async () => {
    try {
      const response = await axios.get(`${API}/rooms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoomsData(response.data);
      toast.success("Rooms refreshed", { duration: 1500 });
    } catch (error) {
      toast.error("Failed to refresh rooms");
      console.error(error);
    }
  };

  useEffect(() => {
    fetchRooms();
    // Refresh room list every 60 seconds (reduced from 30s to prevent blinking)
    const interval = setInterval(fetchRooms, 60000);
    return () => clearInterval(interval);
  }, [token]);

  // Fetch movie now playing for Movie Theater
  useEffect(() => {
    const fetchMovieNowPlaying = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API}/movie/now-playing?room_id=movie-theater`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMovieNowPlaying(response.data.now_playing);
      } catch (error) {
        console.log("No movie playing");
        setMovieNowPlaying(null);
      }
    };
    fetchMovieNowPlaying();
    // Refresh every 30 seconds
    const interval = setInterval(fetchMovieNowPlaying, 30000);
    return () => clearInterval(interval);
  }, [token]);

  // Check Elite VIP status and show memo if not seen
  useEffect(() => {
    const checkEliteVIPStatus = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API}/elite-vip/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.is_elite_vip && !response.data.memo_seen) {
          setEliteVIPMemo(response.data.memo);
          setShowEliteVIPMemo(true);
        }
      } catch (error) {
        console.error("Failed to check Elite VIP status:", error);
      }
    };
    checkEliteVIPStatus();
  }, [token]);

  // Mark Elite VIP memo as seen
  const markMemoSeen = async () => {
    try {
      await axios.post(`${API}/elite-vip/mark-memo-seen`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowEliteVIPMemo(false);
    } catch (error) {
      console.error("Failed to mark memo as seen:", error);
      setShowEliteVIPMemo(false);
    }
  };

  // Connect PM WebSocket when component mounts
  // NOTE: PM/notification WebSocket is now handled GLOBALLY by GlobalPMNotifications in App.js
  // (subscribes once across every authenticated route, with 20s heartbeat).

  // Get current category rooms
  const currentCategory = roomsData.categories[activeTab] || { name: "", rooms: [] };  
  // Filter rooms by search term and sort by user count (most users first)
  const filteredRooms = (currentCategory.rooms?.filter(room => 
    room.name.toLowerCase().includes(searchTerm.toLowerCase())
  ) || []).sort((a, b) => (b.user_count || 0) - (a.user_count || 0));

  const handleEnterRoom = (room) => {
    // Check access
    if (!room.can_access) {
      setShowAccessDenied(room);
      return;
    }
    
    // Check if full (staff can still enter)
    if (room.is_full && !user?.is_admin && !user?.is_moderator) {
      toast.error(`Room is full (${room.capacity}/${room.capacity}). Please try again later.`);
      return;
    }
    
    // If the user has aliases, let them pick which name to appear as (+ online-light toggle).
    if (myAliases.length > 0) {
      setPendingRoom(room);
      return;
    }
    // Default identity: real username, online light on.
    try { localStorage.setItem("megachat_room_identity", JSON.stringify({ alias: null, show_online: true })); } catch (e) {}
    navigate(`/room/${room.room_id}`);
  };

  const enterRoomWithIdentity = (identity) => {
    try { localStorage.setItem("megachat_room_identity", JSON.stringify(identity)); } catch (e) {}
    const room = pendingRoom;
    setPendingRoom(null);
    if (room) navigate(`/room/${room.room_id}`);
  };

  const handleRequestAccess = async (room) => {
    try {
      await axios.post(`${API}/rooms/${room.room_id}/request-access`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Access request submitted! An admin will review your request.");
      setShowAccessDenied(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    }
  };

  const handleLogout = () => {
    // Set a flag to prevent login sound from playing during logout redirect
    sessionStorage.setItem('isLoggingOut', 'true');
    sessionStorage.removeItem('loginSoundPlayed');
    logout();
    toast.success("Logged out successfully");
  };

  const handleSubmitApplication = async () => {
    if (!applicationReason.trim()) {
      toast.error("Please provide a reason for your application");
      return;
    }
    
    setSubmittingApplication(true);
    try {
      await axios.post(`${API}/applications/submit`, {
        application_type: showApplicationModal,
        reason: applicationReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Your ${showApplicationModal} application has been submitted!`);
      setShowApplicationModal(null);
      setApplicationReason("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setSubmittingApplication(false);
    }
  };

  const getCategoryIcon = (categoryId) => {
    switch (categoryId) {
      case "world_chat": return <Globe className="w-4 h-4" />;
      case "assorted_topic": return <MessageCircle className="w-4 h-4" />;
      case "help_service": return <HelpCircle className="w-4 h-4" />;
      default: return <MessageSquare className="w-4 h-4" />;
    }
  };

  const getRoomBadge = (room) => {
    if (room.is_full) {
      return <span className="px-1.5 py-0.5 bg-red-600 text-white text-[10px] rounded font-bold">FULL</span>;
    }
    if (room.restricted === "super_admin") {
      return <span className="px-1.5 py-0.5 bg-purple-600 text-white text-[10px] rounded font-bold">OWNER</span>;
    }
    if (room.restricted === "staff") {
      return <span className="px-1.5 py-0.5 bg-blue-600 text-white text-[10px] rounded font-bold">STAFF</span>;
    }
    if (room.restricted === "vip") {
      return <span className="px-1.5 py-0.5 bg-yellow-500 text-black text-[10px] rounded font-bold">VIP</span>;
    }
    if (room.emergency) {
      return <span className="px-1.5 py-0.5 bg-red-600 text-white text-[10px] rounded font-bold animate-pulse">911</span>;
    }
    if (room.age_restricted) {
      return <span className="px-1.5 py-0.5 bg-gray-700 text-white text-[10px] rounded font-bold">18+</span>;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#C0C0C0] p-2 relative overflow-x-hidden max-w-[100vw]">
      {/* Subtle background watermark logo */}
      <img
        src="https://customer-assets.emergentagent.com/job_80904b6f-7286-4784-ad3d-292b160c5a80/artifacts/crjane52_MEGA%20CHAT%20LOGO.png"
        alt=""
        aria-hidden="true"
        className="fixed bottom-0 right-0 w-[55vw] max-w-[420px] h-auto object-contain opacity-[0.06] pointer-events-none select-none z-0"
      />
      {/* Main Window */}
      <div className="retro-window w-full max-w-6xl mx-auto overflow-x-hidden" data-testid="lobby-window">
        {/* Title Bar */}
        <div className="retro-title-bar">
          <span className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_80904b6f-7286-4784-ad3d-292b160c5a80/artifacts/crjane52_MEGA%20CHAT%20LOGO.png" 
              alt="MEGA Chat"
              className="w-8 h-8 object-contain"
            />
            <span className="site-title-roomlist text-sm">Room List</span>
          </span>
          <div className="flex gap-0.5">
            <button 
              className="w-6 h-5 flex items-center justify-center bg-[#C0C0C0] border border-t-white border-l-white border-b-[#808080] border-r-[#808080] text-xs font-bold hover:bg-[#D4D4D4]"
              title="Minimize"
            >
              _
            </button>
            <button 
              className="w-6 h-5 flex items-center justify-center bg-[#C0C0C0] border border-t-white border-l-white border-b-[#808080] border-r-[#808080] text-xs hover:bg-[#D4D4D4]"
              onClick={toggleFullscreen}
              title={isFullscreen ? "Restore" : "Maximize"}
            >
              {isFullscreen ? "❐" : "□"}
            </button>
            <button 
              className="w-6 h-5 flex items-center justify-center bg-[#C0C0C0] border border-t-white border-l-white border-b-[#808080] border-r-[#808080] text-xs font-bold hover:bg-red-400"
              onClick={handleLogout}
              data-testid="logout-btn"
              title="Close"
            >
              ×
            </button>
          </div>
        </div>

        {/* Menu Bar */}
        <MenuBar 
          onLogout={handleLogout}
          onRefresh={fetchRoomsManual}
          onShowFriends={() => setShowFriends(!showFriends)}
          onShowMessages={() => setShowMessages(!showMessages)}
          onShowAdmin={() => setShowAdmin(!showAdmin)}
          onShowTOS={() => setShowTOS(true)}
          onShowHelp={() => setShowHelp(true)}
          onShowContact={() => setShowContact(true)}
          onShowMyAccount={(tab) => { setMyAccountInitialTab(typeof tab === "string" ? (tab === "password" ? "security" : tab) : "overview"); setShowMyAccount(true); }}
          onShowDonate={() => setShowDonateModal(true)}
          onToggleFullscreen={toggleFullscreen}
          isFullscreen={isFullscreen}
        />

        {/* Mobile hamburger bar — consolidates all nav into one menu so the room list stays clean */}
        <div className="md:hidden bg-[#C0C0C0] border-b border-[#808080] px-2 py-1 flex items-center gap-2">
          <MobileMenu
            label="Menu"
            testid="lobby-mobile-menu"
            buttonClassName="bg-gradient-to-r from-purple-200 to-yellow-200 border-purple-400"
            items={[
              { label: "Friends", icon: <UserPlus className="w-4 h-4 text-orange-600" />, onClick: () => setShowFriends(true), testid: "m-friends" },
              { label: "Find Friends", icon: <Search className="w-4 h-4 text-teal-700" />, onClick: () => setShowUserSearch(true), testid: "m-find-friends" },
              { label: "Messages", icon: <MessageSquare className="w-4 h-4 text-blue-600" />, onClick: () => setShowMessages(true), badge: pmUnreadCount, testid: "m-messages" },
              { label: "Messenger", icon: <MessageCircle className="w-4 h-4 text-purple-600" />, onClick: () => { setShowMessenger(true); setMessengerUnread(0); if ("clearAppBadge" in navigator) { try { navigator.clearAppBadge(); } catch (_) {} } }, badge: messengerUnread, testid: "m-messenger" },
              { label: "Profile", icon: <User className="w-4 h-4 text-teal-600" />, onClick: () => navigate('/profile'), testid: "m-profile" },
              { label: "Aliases", icon: <UserCircle className="w-4 h-4 text-purple-600" />, onClick: () => setShowAliasManager(true), testid: "m-aliases" },
              { label: "Account", icon: <Diamond className="w-4 h-4 text-cyan-600" />, onClick: () => setShowMyAccount(true), testid: "m-account" },
              { label: "Avatar", icon: <span className="text-base">🎨</span>, onClick: () => setShowAvatarBuilder(true), hidden: user?.is_guest, testid: "m-avatar" },
              { label: "Donate", icon: <Heart className="w-4 h-4 text-red-500" />, onClick: () => setShowDonateModal(true), testid: "m-donate" },
              { label: "Admin", icon: <Shield className="w-4 h-4 text-purple-700" />, onClick: () => setShowAdmin(true), hidden: !user?.is_admin, testid: "m-admin" },
              { label: "Staff Mail", icon: <Mail className="w-4 h-4 text-green-600" />, onClick: () => setShowStaffMail(true), hidden: !(user?.is_admin || user?.is_moderator), testid: "m-mail" },
              { label: "My Tasks", icon: <ClipboardList className="w-4 h-4 text-amber-600" />, onClick: () => setShowStaffTasks(true), hidden: !(user?.is_admin || user?.is_moderator), testid: "m-tasks" },
            ]}
          />
          <div className="flex-1" />
          <button
            className="retro-button flex items-center gap-1 text-xs px-2 py-0.5 relative bg-gradient-to-r from-yellow-100 to-purple-100 border-purple-500"
            onClick={() => { setShowMessenger(true); setMessengerUnread(0); if ("clearAppBadge" in navigator) { try { navigator.clearAppBadge(); } catch (_) {} } }}
            data-testid="mobile-messenger-btn"
            title="Messenger"
          >
            <span className="text-sm">💬</span>
            {messengerUnread > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white">
                {messengerUnread > 99 ? "99+" : messengerUnread}
              </span>
            )}
          </button>
        </div>

        {/* Compact Toolbar - desktop/tablet only (mobile uses the hamburger above). flex-wrap keeps
             all buttons reachable on medium widths. */}
        <div className="hidden md:flex bg-[#C0C0C0] border-b border-[#808080] px-2 py-1 items-center gap-1 flex-wrap overflow-x-auto">
          <button 
            ref={friendsBtnRef}
            className="retro-button flex items-center gap-1 text-xs px-2 py-0.5 font-semibold bg-gradient-to-r from-orange-200 to-amber-200 border-orange-400"
            onClick={() => setShowFriends(!showFriends)}
            data-testid="friends-btn"
            title="Friends List"
          >
            <UserPlus className="w-3 h-3 text-orange-600" />
            <span className="text-orange-800 hidden sm:inline">Friends</span>
          </button>
          <button 
            className="retro-button flex items-center gap-1 text-xs px-2 py-0.5 font-semibold bg-gradient-to-r from-teal-200 to-cyan-200 border-teal-400"
            onClick={() => setShowUserSearch(true)}
            data-testid="find-friends-btn"
            title="Find friends by username"
          >
            <Search className="w-3 h-3 text-teal-700" />
            <span className="text-teal-800 hidden sm:inline">Find Friends</span>
          </button>
          <button 
            ref={messagesBtnRef}
            className="retro-button flex items-center gap-1 text-xs px-2 py-0.5 font-semibold bg-gradient-to-r from-blue-200 to-indigo-200 border-blue-400 relative"
            onClick={() => setShowMessages(!showMessages)}
            data-testid="messages-btn"
            title="Messages"
          >
            <MessageSquare className="w-3 h-3 text-blue-600" />
            <span className="text-blue-800 hidden sm:inline">Messages</span>
            {pmUnreadCount > 0 && (
              <span
                className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center border border-white"
                data-testid="pm-unread-badge"
              >
                {pmUnreadCount > 99 ? "99+" : pmUnreadCount}
              </span>
            )}
          </button>
          {user?.is_admin && (
            <button 
              className="retro-button flex items-center gap-1 text-xs px-2 py-0.5 bg-gradient-to-r from-purple-500 to-violet-600 text-white border-purple-700"
              onClick={() => setShowAdmin(true)}
              data-testid="admin-btn"
              title="Admin Panel"
            >
              <Shield className="w-3 h-3" />
              <span className="hidden sm:inline">Admin</span>
            </button>
          )}
          {/* Staff Buttons - Show for admins, mods, and employees */}
          {(user?.is_admin || user?.is_moderator) && (
            <>
              <button 
                className="retro-button flex items-center gap-1 text-xs px-2 py-0.5 bg-gradient-to-r from-green-200 to-emerald-200 border-green-500"
                onClick={() => setShowStaffMail(true)}
                data-testid="staff-mail-btn"
                title="Staff Mail"
              >
                <Mail className="w-3 h-3 text-green-600" />
                <span className="text-green-800 hidden sm:inline">Mail</span>
              </button>
              <button 
                className="retro-button flex items-center gap-1 text-xs px-2 py-0.5 bg-gradient-to-r from-amber-200 to-yellow-200 border-amber-500"
                onClick={() => setShowStaffTasks(true)}
                data-testid="staff-tasks-btn"
                title="My Tasks"
              >
                <ClipboardList className="w-3 h-3 text-amber-600" />
                <span className="text-amber-800 hidden sm:inline">Tasks</span>
              </button>
            </>
          )}
          <button 
            className="retro-button flex items-center gap-1 text-xs px-2 py-0.5 bg-gradient-to-r from-teal-200 to-cyan-200 border-teal-400"
            onClick={() => navigate('/profile')}
            data-testid="profile-btn"
            title="My Profile"
          >
            <User className="w-3 h-3 text-teal-600" />
            <span className="text-teal-800 hidden sm:inline">Profile</span>
          </button>
          <button 
            className="retro-button flex items-center gap-1 text-xs px-2 py-0.5 bg-gradient-to-r from-purple-200 to-indigo-200 border-purple-400"
            onClick={() => setShowAliasManager(true)}
            data-testid="aliases-btn"
            title="Manage your alias names"
          >
            <UserCircle className="w-3 h-3 text-purple-600" />
            <span className="text-purple-800 hidden sm:inline">Aliases</span>
          </button>
          <div className="flex-1" />
          <button 
            className="retro-button flex items-center gap-1 text-xs px-2 py-0.5 bg-gradient-to-r from-pink-200 to-red-200 border-pink-500"
            onClick={() => setShowDonateModal(true)}
            data-testid="donate-btn"
            title="Donate to MEGA!"
          >
            <Heart className="w-3 h-3 text-red-500" />
            <span className="font-bold text-red-700 hidden sm:inline">DONATE</span>
          </button>
          <button 
            className="retro-button flex items-center gap-1 text-xs px-2 py-0.5 bg-gradient-to-r from-cyan-100 to-blue-100 border-cyan-500"
            onClick={() => setShowMyAccount(true)}
            data-testid="my-account-btn"
            title="My Account - Diamonds & F-Chips"
          >
            <Diamond className="w-3 h-3 text-cyan-600" />
            <span className="hidden sm:inline">Account</span>
          </button>
          {!user?.is_guest && (
            <button
              className="retro-button flex items-center gap-1 text-xs px-2 py-0.5 bg-gradient-to-r from-pink-100 to-purple-100 border-purple-500"
              onClick={() => setShowAvatarBuilder(true)}
              data-testid="open-avatar-btn"
              title="Avatar Creator — build your look & shop with F-Chips"
            >
              <span className="text-sm">🎨</span>
              <span className="hidden sm:inline">Avatar</span>
            </button>
          )}
          <button
            className="retro-button relative flex items-center gap-1 text-xs px-2 py-0.5 bg-gradient-to-r from-yellow-100 to-purple-100 border-purple-500"
            onClick={() => { setShowMessenger(true); setMessengerUnread(0); if ("clearAppBadge" in navigator) { try { navigator.clearAppBadge(); } catch (_) {} } }}
            data-testid="open-messenger-btn"
            title="Open Messenger — buddy list + PMs"
          >
            <span className="text-sm">💬</span>
            <span className="hidden sm:inline">Messenger</span>
            {messengerUnread > 0 && (
              <span
                className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center shadow ring-2 ring-white animate-pulse"
                data-testid="messenger-unread-badge"
              >
                {messengerUnread > 99 ? "99+" : messengerUnread}
              </span>
            )}
          </button>
          
          {/* Extras Dropdown Menu */}
          <div className="relative" ref={extrasMenuRef}>
            <button 
              className="retro-button flex items-center gap-1 text-xs px-2 py-0.5 bg-gradient-to-r from-purple-200 to-indigo-200 border-purple-500"
              onClick={() => setShowExtrasMenu(!showExtrasMenu)}
              data-testid="extras-menu-btn"
              title="Extras - Voting, Tournaments, Leaderboard, Badges"
            >
              <Trophy className="w-3 h-3 text-purple-600" />
              <span className="hidden sm:inline">Extras</span>
              <ChevronDown className={`w-3 h-3 transition-transform ${showExtrasMenu ? 'rotate-180' : ''}`} />
            </button>
            
            {showExtrasMenu && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-xl border border-gray-300 z-50 overflow-hidden">
                <button
                  onClick={() => { setActiveExtrasPanel('voting'); setShowExtrasMenu(false); }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-purple-100 flex items-center gap-2 border-b border-gray-200"
                >
                  <Vote className="w-4 h-4 text-purple-500" />
                  <span>Voting</span>
                </button>
                <button
                  onClick={() => { setActiveExtrasPanel('tournaments'); setShowExtrasMenu(false); }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-purple-100 flex items-center gap-2 border-b border-gray-200"
                >
                  <Trophy className="w-4 h-4 text-yellow-500" />
                  <span>Tournaments</span>
                </button>
                <button
                  onClick={() => { setActiveExtrasPanel('leaderboard'); setShowExtrasMenu(false); }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-purple-100 flex items-center gap-2 border-b border-gray-200"
                >
                  <Medal className="w-4 h-4 text-green-500" />
                  <span>Leaderboard</span>
                </button>
                <button
                  onClick={() => { setActiveExtrasPanel('badges'); setShowExtrasMenu(false); }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-purple-100 flex items-center gap-2"
                >
                  <Award className="w-4 h-4 text-blue-500" />
                  <span>Badges</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* User Info Bar */}
        <div className="bg-[#FFCC00] px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-3 flex-wrap">
            <div 
              className="w-8 h-8 rounded-sm flex items-center justify-center text-white font-bold text-sm"
              style={{ backgroundColor: user?.avatar_color || "#7B0099" }}
            >
              {user?.username?.[0]?.toUpperCase()}
            </div>
            <div>
              <p className="font-verdana text-sm font-bold flex items-center gap-1" data-testid="user-display-name">
                {user?.username}
                {user?.is_admin && <Shield className="w-3 h-3 text-[#7B0099]" />}
              </p>
              <p className="font-arial text-xs text-[#666]">Online</p>
            </div>
            <StatusSelector />

            {/* Compact "Apply for Team" controls moved up from the body so we save vertical space */}
            <div className="flex items-center gap-1 ml-2 pl-2 border-l border-[#999]">
              {!user?.is_moderator && !user?.is_admin && (
                <button
                  className="retro-button flex items-center gap-1 text-[10px] px-2 py-1 bg-[#000080] text-white border-[#000080]"
                  onClick={() => setShowApplicationModal('moderator')}
                  data-testid="apply-moderator-btn"
                  title="Apply for Moderator"
                >
                  <Award className="w-3 h-3" />
                  Apply Mod
                </button>
              )}
              {!user?.is_admin && (
                <button
                  className="retro-button flex items-center gap-1 text-[10px] px-2 py-1 bg-[#7B0099] text-white border-[#7B0099]"
                  onClick={() => setShowApplicationModal('admin')}
                  data-testid="apply-admin-btn"
                  title="Apply for Admin"
                >
                  <Shield className="w-3 h-3" />
                  Apply Admin
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* "You are an Admin/Moderator" badge moved up here next to search */}
            {(user?.is_moderator || user?.is_admin) && (
              <span className="font-arial text-[11px] text-[#008000] font-bold flex items-center gap-1 bg-green-50 px-2 py-1 border border-green-300 rounded">
                <Star className="w-3 h-3 text-[#FFD700]" />
                You are {user?.is_admin ? 'an Admin' : 'a Moderator'}
              </span>
            )}
            <Search className="w-4 h-4 text-[#666]" />
            <input
              type="text"
              placeholder="Search rooms..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="retro-input w-48 text-xs"
              data-testid="room-search-input"
            />
          </div>
        </div>

        {/* Content Area */}
        <div className="flex">
          {/* Main Content */}
          <div className="flex-1 min-w-0 overflow-x-hidden p-4 bg-[#C0C0C0]">

            {/* Guest mode banner */}
            {user?.is_guest && (
              <div
                className="mb-3 rounded-lg border-2 border-[#B8860B] bg-gradient-to-r from-[#FFF3C4] to-[#FFE08A] px-4 py-2.5 flex items-center justify-between gap-3 shadow"
                data-testid="guest-mode-banner"
              >
                <p className="font-arial text-xs text-[#7a5c00]">
                  👤 You're browsing as a <b>guest</b>. You can only chat in <b>GUEST CHAT</b>. Create a free account to unlock all {roomsData.total_rooms || ""} rooms, games, webcams &amp; more!
                </p>
                <button
                  className="retro-button whitespace-nowrap text-xs px-3 py-1 bg-[#7B0099] text-white border-[#7B0099] font-bold"
                  onClick={() => setShowGuestUpgrade(true)}
                  data-testid="guest-register-btn"
                >
                  Register Free
                </button>
              </div>
            )}

            {/* Category Tabs - reordered per user request: Entertainment, Assorted Topic, World, Adult, Help */}
            <div className="flex border-b-2 border-[#808080] mb-2 overflow-x-auto retro-scrollbar" data-testid="category-tabs">
              {(() => {
                const desiredOrder = ['entertainment', 'assorted_topic', 'world_chat', 'adult_only', 'help_service'];
                let entries = Object.entries(roomsData.categories);
                // Guests: only show categories that actually have a room they can enter
                if (user?.is_guest) {
                  entries = entries.filter(([cid, c]) =>
                    (c.rooms && c.rooms.length) ||
                    (c.subcategories && Object.values(c.subcategories).some((s) => s.rooms && s.rooms.length))
                  );
                }
                entries.sort(([a], [b]) => {
                  const ia = desiredOrder.indexOf(a);
                  const ib = desiredOrder.indexOf(b);
                  return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
                });
                return entries.map(([categoryId, category]) => (
                  <button
                    key={categoryId}
                    className={`flex items-center gap-2 px-3 sm:px-4 py-2 font-verdana text-xs sm:text-sm font-bold transition-colors whitespace-nowrap flex-shrink-0 ${
                      activeTab === categoryId
                        ? 'bg-[#FFCC00] text-[#7B0099] border-t-2 border-l-2 border-r-2 border-[#808080] -mb-[2px]'
                        : 'bg-[#C0C0C0] text-[#333] hover:bg-[#E0E0E0]'
                    }`}
                    onClick={() => { setActiveTab(categoryId); setOpenMenuCat((p) => (p === categoryId ? null : categoryId)); }}
                    data-testid={`tab-${categoryId}`}
                  >
                    {getCategoryIcon(categoryId)}
                    {category.name}
                  </button>
                ));
              })()}
            </div>

            {/* Pop-out dropdown: rooms for the selected category, click to jump straight in */}
            {openMenuCat && roomsData.categories[openMenuCat] && (() => {
              const cat = roomsData.categories[openMenuCat];
              const flat = [];
              const seenIds = new Set();
              const pushUnique = (r) => { if (r && !seenIds.has(r.room_id)) { seenIds.add(r.room_id); flat.push(r); } };
              if (cat.subcategories) {
                Object.values(cat.subcategories).forEach((sc) => (sc.rooms || []).forEach(pushUnique));
              } else {
                (cat.rooms || []).forEach(pushUnique);
              }
              const list = flat
                .filter((r) => r.name.toLowerCase().includes(searchTerm.toLowerCase()))
                .sort((a, b) => (b.user_count || 0) - (a.user_count || 0));
              return (
                <div className="relative z-30" data-testid="room-dropdown-menu">
                  <div className="absolute left-0 right-0 bg-[#C0C0C0] border-2 border-[#808080] shadow-2xl max-h-80 overflow-y-auto retro-scrollbar">
                    <div className="sticky top-0 flex items-center justify-between bg-[#000080] text-white px-2 py-1 text-xs font-bold">
                      <span>{cat.name} — pick a room</span>
                      <button onClick={() => setOpenMenuCat(null)} data-testid="close-room-dropdown" className="hover:bg-white/20 px-1">✕</button>
                    </div>
                    {list.length === 0 ? (
                      <p className="text-xs text-center text-[#808080] p-3">No rooms found</p>
                    ) : list.map((room) => (
                      <button
                        key={room.room_id}
                        onClick={() => { handleEnterRoom(room); setOpenMenuCat(null); }}
                        className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left text-xs font-verdana border-b border-[#B0B0B0] hover:bg-[#FFCC00] hover:text-[#7B0099]"
                        data-testid={`dropdown-room-${room.room_id}`}
                      >
                        <span className="flex items-center gap-1 truncate">
                          {!room.can_access && <Lock className="w-3 h-3 text-red-500 flex-shrink-0" />}
                          <span className="truncate">{room.name}</span>
                        </span>
                        <span className={`flex items-center gap-1 flex-shrink-0 ${room.is_full ? 'text-red-600 font-bold' : 'text-[#606060]'}`}>
                          <Users className="w-3 h-3" />{room.user_count}/{room.capacity}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Rooms list — taller now that the team-apply row is gone, room cards are more compact */}
            <div className="retro-inset p-2 sm:p-3 h-[calc(100vh-260px)] min-h-[360px] overflow-y-auto retro-scrollbar">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-verdana text-lg font-bold text-[#7B0099]">
                  {currentCategory.name || "Chat Rooms"}
                </h2>
                <span className="font-arial text-xs text-[#666]">
                  Room Capacity: {roomsData.capacity} users
                </span>
              </div>
              
              {loading ? (
                <div className="text-center py-8">
                  <p className="font-arial text-sm">Loading rooms...</p>
                </div>
              ) : currentCategory.subcategories ? (
                /* Render subcategories (Entertainment) */
                <div className="space-y-4" data-testid="subcategories-view">
                  {Object.entries(currentCategory.subcategories).map(([subcatId, subcat]) => {
                    const subcatRooms = (subcat.rooms || [])
                      .filter(room => room.name.toLowerCase().includes(searchTerm.toLowerCase()))
                      .sort((a, b) => (b.user_count || 0) - (a.user_count || 0));
                    
                    if (searchTerm && subcatRooms.length === 0) return null;
                    
                    const isExpanded = expandedSubcategory === subcatId || !expandedSubcategory;
                    
                    return (
                      <div key={subcatId} className="retro-window" data-testid={`subcategory-${subcatId}`}>
                        <div 
                          className="retro-title-bar cursor-pointer flex items-center justify-between"
                          onClick={() => setExpandedSubcategory(expandedSubcategory === subcatId ? null : subcatId)}
                        >
                          <span className="font-bold text-sm">{subcat.name}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs opacity-80">{subcatRooms.length} rooms</span>
                            <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="p-2 bg-[#E8E8E8]">
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                              {subcatRooms.map((room) => (
                                <div
                                  key={room.room_id}
                                  className={`room-card min-w-0 hover:bg-[#D0D0D0] cursor-pointer relative ${
                                    room.is_full && !user?.is_admin && !user?.is_moderator ? 'opacity-60' : ''
                                  } ${!room.can_access ? 'border-l-4 border-l-red-500' : ''}`}
                                  onClick={() => handleEnterRoom(room)}
                                  data-testid={`room-card-${room.room_id}`}
                                >
                                  <div className="flex items-start justify-between gap-1">
                                    <p className="font-verdana text-xs font-bold text-[#000080] flex-1 min-w-0 break-words">
                                      {room.name}
                                    </p>
                                    {!room.can_access && <Lock className="w-3 h-3 text-red-500 flex-shrink-0" />}
                                  </div>
                                  
                                  {/* Now Playing banner for Movie Theaters */}
                                  {(room.room_id === 'movie-theater' || room.room_id.startsWith('movie-theater-')) && movieNowPlaying && (
                                    <div className="mt-1 bg-gradient-to-r from-red-600 to-red-500 rounded px-2 py-1 text-white text-[10px] flex items-center gap-1 animate-pulse">
                                      <Film className="w-3 h-3" />
                                      <span className="font-bold truncate">NOW PLAYING</span>
                                    </div>
                                  )}
                                  
                                  <div className="flex items-center justify-between mt-2">
                                    <div className="flex items-center gap-1">
                                      <Users className="w-3 h-3 text-[#808080]" />
                                      <span className={`font-arial text-xs ${room.is_full ? 'text-red-600 font-bold' : 'text-[#808080]'}`}>
                                        {room.user_count}/{room.capacity}
                                      </span>
                                    </div>
                                    {getRoomBadge(room)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2" data-testid="rooms-grid">
                  {filteredRooms.map((room) => (
                    <div
                      key={room.room_id}
                      className={`room-card min-w-0 hover:bg-[#E0E0E0] cursor-pointer relative ${
                        room.is_full && !user?.is_admin && !user?.is_moderator ? 'opacity-60' : ''
                      } ${!room.can_access ? 'border-l-4 border-l-red-500' : ''} ${
                        room.emergency ? 'border-2 border-red-500 bg-red-50' : ''
                      }`}
                      onClick={() => handleEnterRoom(room)}
                      data-testid={`room-card-${room.room_id}`}
                    >
                      <div className="flex items-start justify-between gap-1">
                        <p className="font-verdana text-xs font-bold text-[#000080] flex-1 min-w-0 break-words">
                          {room.name}
                        </p>
                        {!room.can_access && <Lock className="w-3 h-3 text-red-500 flex-shrink-0" />}
                      </div>
                      
                      {/* Now Playing banner for Movie Theater */}
                      {room.room_id === 'movie-theater' && movieNowPlaying && (
                        <div className="mt-1 bg-gradient-to-r from-red-600 to-red-500 rounded px-2 py-1 text-white text-[10px] flex items-center gap-1 animate-pulse">
                          <Film className="w-3 h-3" />
                          <span className="font-bold truncate">NOW: {movieNowPlaying.video_title}</span>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-1">
                          <Users className="w-3 h-3 text-[#808080]" />
                          <span className={`font-arial text-xs ${room.is_full ? 'text-red-600 font-bold' : 'text-[#808080]'}`}>
                            {room.user_count}/{room.capacity}
                          </span>
                        </div>
                        {getRoomBadge(room)}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!loading && !currentCategory.subcategories && filteredRooms.length === 0 && (
                <div className="text-center py-8">
                  <p className="font-arial text-sm text-[#808080]">
                    {searchTerm ? `No rooms found matching "${searchTerm}"` : "No rooms in this category"}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Side Panels — desktop: anchored draggable popovers; mobile: full-screen bottom-sheet modals */}
          {isMobileNav ? (
            <>
              <MobileSheet open={showFriends} onClose={() => setShowFriends(false)} testid="friends-sheet">
                <FriendsPanel
                  fullWidth
                  onClose={() => setShowFriends(false)}
                  onStartPM={handleOpenPM}
                  onViewProfile={(u) => navigate(`/profile/${u.user_id}`)}
                />
              </MobileSheet>
              <MobileSheet open={showMessages} onClose={() => setShowMessages(false)} testid="messages-sheet">
                <PrivateMessagesPanel fullWidth onClose={() => setShowMessages(false)} onOpenPM={handleOpenPM} />
              </MobileSheet>
            </>
          ) : (
            <>
              <AnchoredPopover open={showFriends} anchorRef={friendsBtnRef} onOutsideClick={() => setShowFriends(false)}>
                <FriendsPanel
                  draggable
                  onClose={() => setShowFriends(false)}
                  onStartPM={handleOpenPM}
                  onViewProfile={(u) => navigate(`/profile/${u.user_id}`)}
                />
              </AnchoredPopover>
              <AnchoredPopover open={showMessages} anchorRef={messagesBtnRef} onOutsideClick={() => setShowMessages(false)}>
                <PrivateMessagesPanel onClose={() => setShowMessages(false)} onOpenPM={handleOpenPM} />
              </AnchoredPopover>
            </>
          )}
          {showUserSearch && (
            <UserSearchModal
              onClose={() => setShowUserSearch(false)}
              onMessage={(u) => { setShowUserSearch(false); handleOpenPM?.(u); }}
            />
          )}
        </div>

        {/* Status Bar */}
        <div className="bg-[#C0C0C0] border-t border-[#FFFFFF] px-2 py-1 flex justify-between">
          <span className="font-arial text-xs">
            {Object.values(roomsData.categories).reduce((acc, cat) => {
              if (cat.subcategories) {
                return acc + Object.values(cat.subcategories).reduce((subAcc, subcat) => subAcc + (subcat.rooms?.length || 0), 0);
              }
              return acc + (cat.rooms?.length || 0);
            }, 0)} rooms available | created by mr_fuker
          </span>
          <span className="font-arial text-xs">
            {Object.values(roomsData.categories).reduce((acc, cat) => {
              if (cat.subcategories) {
                return acc + Object.values(cat.subcategories).reduce((subAcc, subcat) => 
                  subAcc + (subcat.rooms?.reduce((a, r) => a + r.user_count, 0) || 0), 0);
              }
              return acc + (cat.rooms?.reduce((a, r) => a + r.user_count, 0) || 0);
            }, 0)} users online
          </span>
        </div>
      </div>

      {/* Admin Panel Modal */}
      {showAdmin && <AdminPanel onClose={() => setShowAdmin(false)} onStartPM={handleOpenPM} />}

      {/* My Account Panel */}
      {showMyAccount && <MyAccountPanel onClose={() => setShowMyAccount(false)} initialTab={myAccountInitialTab} />}
      {showMessenger && (
        isMessengerMobile ? (
          // On phones: full-screen overlay sized to the DYNAMIC viewport so the
          // composer never hides behind the browser URL/nav bar.
          <div
            className="fixed inset-x-0 top-0 z-[9500] bg-white flex flex-col overflow-hidden"
            style={{ height: "100dvh" }}
            data-testid="messenger-modal-mobile"
          >
            <MessengerPage onClose={() => setShowMessenger(false)} />
          </div>
        ) : (
          <DraggableResizableWindow
            title={<span className="text-xs font-bold">💬 MEGA CHAT · Messenger</span>}
            onClose={() => setShowMessenger(false)}
            initialPosition={{ x: Math.max(20, (window.innerWidth - 560) / 2), y: 70 }}
            initialSize={{ width: 560, height: 440 }}
            minSize={{ width: 420, height: 340 }}
            maxSize={{ width: 1200, height: 900 }}
            zIndex={9500}
            titleBarColor="bg-gradient-to-r from-purple-900 to-yellow-700"
          >
            <div className="h-full w-full" data-testid="messenger-modal-desktop">
              <MessengerPage onClose={() => setShowMessenger(false)} />
            </div>
          </DraggableResizableWindow>
        )
      )}

      {/* Donate Modal */}
      {showDonateModal && <DonateModal onClose={() => setShowDonateModal(false)} />}
      {showGuestUpgrade && <GuestUpgradeModal onClose={() => setShowGuestUpgrade(false)} />}
      {showAvatarBuilder && <AvatarBuilder onClose={() => setShowAvatarBuilder(false)} />}

      {/* Help Pages */}
      {showTOS && <TOSModal onClose={() => setShowTOS(false)} />}
      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
      {showContact && <ContactModal onClose={() => setShowContact(false)} token={token} />}

      {/* Access Denied Modal */}
      {showAccessDenied && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="retro-window w-full max-w-md" data-testid="access-denied-modal">
            <div className="retro-title-bar">
              <span className="flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Access Restricted
              </span>
              <button 
                className="close-button-visible"
                onClick={() => setShowAccessDenied(null)}
                data-testid="close-access-modal"
              >
                ✕
              </button>
            </div>
            
            <div className="p-4 bg-[#C0C0C0]">
              <div className="retro-inset p-3 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <span className="font-verdana text-sm font-bold text-red-600">Access Denied</span>
                </div>
                <p className="font-arial text-sm">
                  <strong>{showAccessDenied.name}</strong> is a restricted room.
                </p>
                <p className="font-arial text-sm mt-2">
                  {showAccessDenied.restricted === "super_admin" && (
                    "This room is only accessible to the site owner."
                  )}
                  {showAccessDenied.restricted === "staff" && (
                    "This room is only accessible to Moderators and Admins."
                  )}
                  {showAccessDenied.restricted === "vip" && (
                    "This room is only accessible to VIP members and staff."
                  )}
                </p>
              </div>
              
              <div className="flex gap-2">
                {showAccessDenied.restricted === "super_admin" && (
                  <button
                    className="retro-button flex-1 py-2 bg-[#7B0099] text-white border-[#7B0099] font-bold"
                    onClick={() => handleRequestAccess(showAccessDenied)}
                  >
                    Request Access
                  </button>
                )}
                <button
                  className="retro-button flex-1 py-2"
                  onClick={() => setShowAccessDenied(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Application Modal */}
      {showApplicationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="retro-window w-full max-w-md" data-testid="application-modal">
            <div className="retro-title-bar">
              <span className="flex items-center gap-2">
                {showApplicationModal === 'admin' ? (
                  <Shield className="w-4 h-4" />
                ) : (
                  <Award className="w-4 h-4" />
                )}
                Apply for {showApplicationModal === 'admin' ? 'Admin' : 'Moderator'}
              </span>
              <button 
                className="close-button-visible"
                onClick={() => {
                  setShowApplicationModal(null);
                  setApplicationReason("");
                }}
                data-testid="close-application-modal"
              >
                ✕
              </button>
            </div>
            
            <div className="p-4 bg-[#C0C0C0]">
              <div className="retro-inset p-3 mb-4">
                <p className="font-arial text-sm mb-2">
                  {showApplicationModal === 'admin' ? (
                    <>
                      <strong>Admin privileges include:</strong><br />
                      • Full control over all users and settings<br />
                      • Approve/deny user registrations<br />
                      • Ban users and IP addresses<br />
                      • Manage moderators and other admins
                    </>
                  ) : (
                    <>
                      <strong>Moderator privileges include:</strong><br />
                      • Kick users from chat rooms<br />
                      • Temporary mute users<br />
                      • Clear inappropriate messages<br />
                      • Report issues to admins
                    </>
                  )}
                </p>
              </div>
              
              <label className="font-verdana text-sm font-bold block mb-2">
                Why do you want to be a {showApplicationModal}?
              </label>
              <textarea
                className="retro-input w-full h-32 text-sm resize-none"
                placeholder="Tell us about yourself and why you'd be a good fit for the team..."
                value={applicationReason}
                onChange={(e) => setApplicationReason(e.target.value)}
                data-testid="application-reason-input"
              />
              
              <div className="flex gap-2 mt-4">
                <button
                  className="retro-button flex-1 py-2 bg-[#008000] text-white border-[#008000] font-bold"
                  onClick={handleSubmitApplication}
                  disabled={submittingApplication}
                  data-testid="submit-application-btn"
                >
                  {submittingApplication ? 'Submitting...' : 'Submit Application'}
                </button>
                <button
                  className="retro-button flex-1 py-2"
                  onClick={() => {
                    setShowApplicationModal(null);
                    setApplicationReason("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PM Windows are now rendered globally in App.js to persist across navigation */}

      {/* Elite VIP Memo Modal */}
      {showEliteVIPMemo && (
        <div className="fixed inset-0 bg-black/80 z-[9999] flex items-center justify-center p-4" data-testid="elite-vip-memo-modal">
          <div className="retro-window max-w-2xl w-full max-h-[90vh] flex flex-col animate-in zoom-in-95">
            <div className="retro-title-bar px-3 py-2 flex items-center gap-2 bg-gradient-to-r from-yellow-500 via-amber-500 to-yellow-600">
              <Crown className="w-5 h-5 text-white" />
              <span className="text-white font-bold">👑 ELITE VIP WELCOME 👑</span>
            </div>
            <div className="p-4 bg-gradient-to-b from-amber-50 to-yellow-50 flex-1 overflow-y-auto retro-scrollbar">
              <pre className="whitespace-pre-wrap text-sm font-mono text-gray-800 leading-relaxed">
                {eliteVIPMemo}
              </pre>
            </div>
            <div className="p-3 bg-gray-100 border-t-2 border-gray-300 flex justify-center">
              <button
                className="retro-button px-8 py-2 bg-gradient-to-r from-yellow-500 to-amber-500 text-white font-bold text-lg border-yellow-600 hover:from-yellow-400 hover:to-amber-400"
                onClick={markMemoSeen}
                data-testid="elite-vip-memo-close-btn"
              >
                ,,l,, (~_~) ,,l,, GOT IT! ,,l,, (~_~) ,,l,,
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Extras Panel Modal */}
      {activeExtrasPanel && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setActiveExtrasPanel(null)}>
          <div className="bg-[#C0C0C0] rounded-lg shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-2 flex items-center justify-between">
              <span className="font-bold">
                {activeExtrasPanel === 'voting' && '🗳️ Voting'}
                {activeExtrasPanel === 'tournaments' && '🏆 Tournaments'}
                {activeExtrasPanel === 'leaderboard' && '🏅 Leaderboard'}
                {activeExtrasPanel === 'badges' && '🎖️ Badges'}
              </span>
              <button onClick={() => setActiveExtrasPanel(null)} className="hover:bg-white/20 p-1 rounded">✕</button>
            </div>
            {/* Panel Content */}
            <div className="max-h-[70vh] overflow-y-auto">
              {activeExtrasPanel === 'voting' && <VotingPanel />}
              {activeExtrasPanel === 'tournaments' && <TournamentPanel />}
              {activeExtrasPanel === 'leaderboard' && <LeaderboardPanel />}
              {activeExtrasPanel === 'badges' && <BadgesPanel />}
            </div>
          </div>
        </div>
      )}

      {/* Staff Mail Panel */}
      {showStaffMail && <StaffMailPanel onClose={() => setShowStaffMail(false)} />}

      {/* Staff Tasks Panel */}
      {showStaffTasks && <StaffTasksPanel onClose={() => setShowStaffTasks(false)} />}

      {/* Alias Manager */}
      {showAliasManager && (
        <AliasManager
          token={token}
          onClose={() => setShowAliasManager(false)}
          onChanged={loadAliases}
        />
      )}

      {/* Room-entry identity picker (only when the user has aliases) */}
      {pendingRoom && (
        <RoomIdentityPicker
          username={user?.username}
          aliases={myAliases}
          room={pendingRoom}
          onCancel={() => setPendingRoom(null)}
          onConfirm={enterRoomWithIdentity}
        />
      )}

      {/* Admin Announcements Popup (shows on login if there are unread announcements) */}
      {showAdminAnnouncements && (
        <AdminAnnouncementPopup 
          onClose={() => {
            setShowAdminAnnouncements(false);
            // After announcements, show offline messages
            setShowOfflineMessages(true);
          }} 
        />
      )}

      {/* Offline Messages Popup */}
      {showOfflineMessages && (
        <OfflineMessagesPopup 
          onClose={() => setShowOfflineMessages(false)}
          onOpenPM={(userId, username) => {
            openPMWindow?.(userId, username);
          }}
        />
      )}
    </div>
  );
};

export default LobbyPage;
